// Gesetzliche Feiertage Deutschland — reine, deterministische Domänen-Logik (I/O-frei, offline-fähig, testbar).
// Fundament für Monats-Soll (Feiertag = Ausfall), §3b-Zuschlagsfenster und §§9-11 ArbZG (Sonn-/Feiertagsarbeit).
//
// BEWUSST KEINE Runtime-/Kalender-API: die beweglichen Feiertage hängen alle an Ostern, das per Gauß-/Meeus-
// Osterformel exakt berechenbar ist. So ist die Berechnung im Werkstatt-Tablet auch offline identisch und
// gegen einen bekannten Testvektor (2026) verifizierbar.
//
// ⚠️ Rechtsstand: die Zuordnung Feiertag↔Bundesland folgt der geltenden Rechtslage (Stand 2026). Zwei Feiertage
// sind gemeinde-abhängig (Fronleichnam in SN/TH, Mariä Himmelfahrt in BY) → über opts.catholicMunicipality
// zuschaltbar, standardmäßig AUS. 24.12. und 31.12. sind KEINE gesetzlichen Feiertage (separate betriebliche
// Sondertage — kommt später).

/** Die 16 Bundesländer (Kfz-Kennzeichen-ähnliche Kürzel, wie im Meldewesen üblich). */
export const GERMAN_STATES = ['BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV', 'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH'] as const
export type FederalState = (typeof GERMAN_STATES)[number]

export const GERMAN_STATE_LABELS: Record<FederalState, string> = {
  BW: 'Baden-Württemberg',
  BY: 'Bayern',
  BE: 'Berlin',
  BB: 'Brandenburg',
  HB: 'Bremen',
  HH: 'Hamburg',
  HE: 'Hessen',
  MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen',
  RP: 'Rheinland-Pfalz',
  SL: 'Saarland',
  SN: 'Sachsen',
  ST: 'Sachsen-Anhalt',
  SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
}

export function isFederalState(v: unknown): v is FederalState {
  return typeof v === 'string' && (GERMAN_STATES as readonly string[]).includes(v)
}

export interface HolidayOptions {
  /** Nur relevant für gemeinde-abhängige Feiertage: Mariä Himmelfahrt (BY) + Fronleichnam (SN/TH) in
   *  überwiegend katholischen Gemeinden. Standard AUS — der Chef schaltet es je Werkstatt bei Bedarf ein. */
  catholicMunicipality?: boolean
}

/** Ostersonntag (gregorianisch) für ein Jahr — Meeus/Jones/Butcher-Algorithmus. Rückgabe als {month(1-12), day}. */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3 = März, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return { month, day }
}

/** Kalender-Tag als 'YYYY-MM-DD' (reine Kalender-Arithmetik in UTC, zeitzonen-neutral). */
function ymd(year: number, month1: number, day: number): string {
  const t = Date.UTC(year, month1 - 1, day)
  const d = new Date(t)
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}-${mm}-${dd}`
}

/** Ostersonntag + Offset-Tage → 'YYYY-MM-DD'. */
function easterPlus(year: number, offsetDays: number): string {
  const e = easterSunday(year)
  const t = Date.UTC(year, e.month - 1, e.day + offsetDays)
  const d = new Date(t)
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}-${mm}-${dd}`
}

/** Buß- und Bettag (nur Sachsen): der Mittwoch vor dem 23.11. (liegt stets im Fenster 16.–22.11.). */
function bussUndBettag(year: number): string {
  // Vom 22.11. rückwärts bis zum ersten Mittwoch (UTC-Wochentag 3).
  for (let day = 22; day >= 16; day--) {
    if (new Date(Date.UTC(year, 10, day)).getUTCDay() === 3) return ymd(year, 11, day)
  }
  return ymd(year, 11, 22) // unerreichbar; defensiver Fallback
}

type Rule = { name: string; date: (y: number) => string; states: readonly FederalState[]; catholicOnly?: FederalState[] }

const ALL: readonly FederalState[] = GERMAN_STATES

// Rechtsstand 2026. Bundesweit = 9 Feiertage; der Rest ist länderspezifisch.
const RULES: Rule[] = [
  { name: 'Neujahr', date: (y) => ymd(y, 1, 1), states: ALL },
  { name: 'Heilige Drei Könige', date: (y) => ymd(y, 1, 6), states: ['BW', 'BY', 'ST'] },
  { name: 'Internationaler Frauentag', date: (y) => ymd(y, 3, 8), states: ['BE', 'MV'] },
  { name: 'Karfreitag', date: (y) => easterPlus(y, -2), states: ALL },
  { name: 'Ostersonntag', date: (y) => easterPlus(y, 0), states: ['BB'] },
  { name: 'Ostermontag', date: (y) => easterPlus(y, 1), states: ALL },
  { name: 'Tag der Arbeit', date: (y) => ymd(y, 5, 1), states: ALL },
  { name: 'Christi Himmelfahrt', date: (y) => easterPlus(y, 39), states: ALL },
  { name: 'Pfingstsonntag', date: (y) => easterPlus(y, 49), states: ['BB'] },
  { name: 'Pfingstmontag', date: (y) => easterPlus(y, 50), states: ALL },
  // Fronleichnam: 6 Länder voll; in SN + TH nur in überwiegend katholischen Gemeinden (opt-in).
  { name: 'Fronleichnam', date: (y) => easterPlus(y, 60), states: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL'], catholicOnly: ['SN', 'TH'] },
  // Mariä Himmelfahrt: SL voll; BY nur in überwiegend katholischen Gemeinden (opt-in).
  { name: 'Mariä Himmelfahrt', date: (y) => ymd(y, 8, 15), states: ['SL'], catholicOnly: ['BY'] },
  { name: 'Weltkindertag', date: (y) => ymd(y, 9, 20), states: ['TH'] },
  { name: 'Tag der Deutschen Einheit', date: (y) => ymd(y, 10, 3), states: ALL },
  { name: 'Reformationstag', date: (y) => ymd(y, 10, 31), states: ['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'] },
  { name: 'Allerheiligen', date: (y) => ymd(y, 11, 1), states: ['BW', 'BY', 'NW', 'RP', 'SL'] },
  { name: 'Buß- und Bettag', date: (y) => bussUndBettag(y), states: ['SN'] },
  { name: '1. Weihnachtstag', date: (y) => ymd(y, 12, 25), states: ALL },
  { name: '2. Weihnachtstag', date: (y) => ymd(y, 12, 26), states: ALL },
]

/**
 * Alle gesetzlichen Feiertage eines Jahres in einem Bundesland als Map 'YYYY-MM-DD' → Name.
 * `opts.catholicMunicipality` schaltet die gemeinde-abhängigen Feiertage (Fronleichnam SN/TH, Mariä Himmelfahrt BY) zu.
 */
export function holidaysForYear(year: number, state: FederalState, opts: HolidayOptions = {}): Map<string, string> {
  const out = new Map<string, string>()
  for (const r of RULES) {
    const applies = r.states.includes(state) || (!!opts.catholicMunicipality && !!r.catholicOnly?.includes(state))
    if (applies) out.set(r.date(year), r.name)
  }
  return out
}

/** Ist der Kalendertag `dayKey` ('YYYY-MM-DD') im gegebenen Bundesland ein gesetzlicher Feiertag? */
export function isPublicHoliday(dayKey: string, state: FederalState, opts: HolidayOptions = {}): boolean {
  const year = Number(dayKey.slice(0, 4))
  if (!Number.isFinite(year)) return false
  return holidaysForYear(year, state, opts).has(dayKey)
}

/** Name des Feiertags am Kalendertag `dayKey`, oder null. */
export function holidayName(dayKey: string, state: FederalState, opts: HolidayOptions = {}): string | null {
  const year = Number(dayKey.slice(0, 4))
  if (!Number.isFinite(year)) return null
  return holidaysForYear(year, state, opts).get(dayKey) ?? null
}

/**
 * Set aller Feiertags-Tage ('YYYY-MM-DD') im geschlossenen Bereich [from,to] für ein Bundesland — für das
 * Monats-Soll (Feiertag = Ausfall). `from`/`to` sind 'YYYY-MM-DD'; deckt jahresübergreifende Zeiträume ab.
 */
export function holidayDaysInRange(from: string, to: string, state: FederalState, opts: HolidayOptions = {}): Set<string> {
  const out = new Set<string>()
  const fromYear = Number(from.slice(0, 4))
  const toYear = Number(to.slice(0, 4))
  if (!Number.isFinite(fromYear) || !Number.isFinite(toYear)) return out
  for (let y = fromYear; y <= toYear; y++) {
    for (const day of holidaysForYear(y, state, opts).keys()) {
      if (day >= from && day <= to) out.add(day)
    }
  }
  return out
}
